import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VALIDATION_ROLES = ["Directeur", "Administrateur national"];

export default function StatusControl({ analysis, onUpdated }) {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parametres`).then((res) => {
      const list = res.data
        .filter((p) => p.CATEGORIE === "STATUT_HFACS")
        .sort((a, b) => Number(a.ORDRE) - Number(b.ORDRE));
      setStatuses(list);
    });
  }, []);

  const currentIndex = statuses.findIndex((s) => s.VALEUR === analysis.STATUT);
  const nextStatus = currentIndex >= 0 ? statuses[currentIndex + 1] : null;
  const canValidate = VALIDATION_ROLES.includes(user?.role);
  const nextBlockedByRole = nextStatus?.VALEUR === "Validée" && !canValidate;

  const applyStatus = async (statut) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/hfacs/${analysis.ID_HFACS}/status`, { statut });
      toast.success(`Statut mis à jour : ${statut}`);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de mettre à jour le statut.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={analysis.STATUT} onValueChange={applyStatus} disabled={updating}>
        <SelectTrigger data-testid="status-select" className="w-auto h-9 font-mono text-xs uppercase gap-2 border-border">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {statuses.map((s) => (
            <SelectItem key={s.VALEUR} value={s.VALEUR} disabled={s.VALEUR === "Validée" && !canValidate}>
              {s.VALEUR}{s.VALEUR === "Validée" && !canValidate ? " (Directeur uniquement)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {nextStatus && (
        <Button
          size="sm"
          variant="outline"
          data-testid="status-next-step-button"
          disabled={updating || nextBlockedByRole}
          onClick={() => applyStatus(nextStatus.VALEUR)}
          title={nextBlockedByRole ? "Seul un Directeur ou un Administrateur national peut valider cette étape." : ""}
          className="gap-2 border-primary/40 text-primary hover:bg-primary/10"
        >
          {updating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
          Passer à : {nextStatus.VALEUR}
        </Button>
      )}
      {nextBlockedByRole && (
        <span data-testid="status-validation-hint" className="text-xs text-muted-foreground">
          Seul un Directeur ou un Administrateur national peut valider.
        </span>
      )}
    </div>
  );
}
